import {
  ICodePanelAPI,
  ICustomisationAPI,
  ICustomisationBaseAPI,
  ICustomisationEmptyAPI,
  IDataAPI,
  IGroupAPI,
  ICssCustomPropertyAPI,
  ICssPropertyAPI,
  IEventAPI,
  IForwardAPI,
  IInlinePanelAPI,
  IUnknownPanelAPI,
  IUrlPanelAPI,
  TPanelAPI,
} from '@wix/editor-platform-host-integration-apis';
import { AppData, ValueOf } from '@wix/document-services-types';
import {
  Action,
  ACTIONS,
  DisplayGroupItem,
  PanelAction,
  DISPLAY_GROUPS,
} from '@wix/component-protocol/schema';
import { ComponentType } from '@wix/ambassador-devcenter-components-v1-component/types';
import { getPlatformInfraAPI } from '../../../bootstrap/init';

import {
  getAppData,
  getComponentAppData,
} from '../../../utils/componentsService/helpers';

import {
  CustomManifestAction,
  isCustomManifestAction,
  PlatformContext,
} from '../types';
import {
  createEditorPlatformEditActionsError,
  EditorPlatformEditActionsErrorCode,
} from '../errors';
import { ForwardAction } from './forwardAction';
import { createActionPanelAPI } from '../actionPanelAPI';
import { platformEvents } from '../../../../platformEvents';

class CustomisationAPI implements ICustomisationBaseAPI {
  constructor(protected action: CustomManifestAction) {}

  hasCustomisation() {
    return !!this.action.execution;
  }
}

class CustomisationEmptyAPI implements ICustomisationEmptyAPI {
  public apiType: 'none' = 'none';

  hasCustomisation() {
    return false;
  }

  getCustomisation() {
    console.warn('There is no customisation for this action');
    return null;
  }
}

class InlinePanelAPI extends CustomisationAPI implements IInlinePanelAPI {
  public apiType: typeof ACTIONS.ACTION_TYPE.panel = ACTIONS.ACTION_TYPE.panel;
}

class UnknownPanelAPI extends InlinePanelAPI implements IUnknownPanelAPI {
  public panelContentType: typeof ACTIONS.PANEL_CONTENT_TYPE.UNKNOWN_PanelContentType =
    ACTIONS.PANEL_CONTENT_TYPE.UNKNOWN_PanelContentType;

  constructor(protected action: CustomManifestAction) {
    super(action);
  }

  getCustomisation() {
    console.warn('Panel content type is not supported');
    return null;
  }
}

class UrlPanelAPI extends InlinePanelAPI implements IUrlPanelAPI {
  public panelContentType: typeof ACTIONS.PANEL_CONTENT_TYPE.url =
    ACTIONS.PANEL_CONTENT_TYPE.url;
  readonly appData?: AppData;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    readonly context: PlatformContext,
  ) {
    super(action);
    this.appData = getAppData(
      context.editorPointer,
      context.documentServicesAPI,
      context.editorPointerAPI,
    );
  }

  getCustomisation() {
    if (!this.appData) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionAppDataIsMissing,
      ).withActionName(this.actionName);
    }

    if (!this.action.execution.panel?.inline?.url) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionPanelUrlIsMissing,
      ).withActionName(this.actionName);
    }

    const metadata = this.context.componentsAPI.components.getMetadata(
      this.context.editorPointer,
    );
    const isLegacySettingsPanel = !!(
      this.actionName === ACTIONS.ACTION_NAME.settings &&
      (metadata?.hasLegacySettingsPanel ?? false)
    );

    const onUnmount = () => {
      const componentRef = this.context.editorPointerAPI.getDocumentPointer(
        this.context.editorPointer,
      );

      this.context.documentServicesAPI.platform.notifyApplicationByAppDefId(
        this.appData!.appDefinitionId!,
        platformEvents.factory.componentSettingsPanelClosed({
          componentRef,
        }),
      );
    };

    return {
      appDefinitionId: this.appData.appDefinitionId!,
      displayName: this.action.displayName!,
      getPlatformInfraAPI,
      url: this.action.execution.panel?.inline?.url,
      panelSize: this.action.execution.panel?.inline?.size,
      isLegacySettingsPanel,
      panelMode: this.action.execution.panel?.inline?.panelMode,
      onUnmount,
    };
  }
}

class TpaUrlPanelAPI extends UrlPanelAPI {
  getCustomisation() {
    const customization = super.getCustomisation();
    let url = customization.url;
    let helpId;

    if (url && this.appData?.appDefinitionId) {
      const compId = this.context.editorPointerAPI.getDocumentPointer(
        this.context.editorPointer,
      ).id;

      const appMarketData =
        this.context.documentServicesAPI.tpa.appMarket.getData(
          this.appData.appDefinitionId!,
        );

      const widgetId = this.context.documentServicesAPI.components.data.get(
        this.context.editorPointers[0].documentPointer,
      ).widgetId;

      const appMarketWidgetData = appMarketData.widgets?.find(
        (widget) => widget.widgetId === widgetId,
      );
      helpId = appMarketWidgetData?.settings?.helpId;

      // mirrors essential-only studio behaviour, studio attaches more data, but this should be sufficient
      url = this.context.documentServicesAPI.tpa.app.url.getSettingsModalUrl(
        {
          appDefinitionId: this.appData.appDefinitionId,
          url,
          origCompId: compId,
        },
        'tpaSettings',
      );
    }

    return {
      ...customization,
      helpId,
      url,
    };
  }
}

class CodePanelAPI extends InlinePanelAPI implements ICodePanelAPI {
  public panelContentType: typeof ACTIONS.PANEL_CONTENT_TYPE.code =
    ACTIONS.PANEL_CONTENT_TYPE.code;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    readonly context: PlatformContext,
  ) {
    super(action);
  }

  getCustomisation() {
    const panelContent = this.context.componentsAPI.panels.getPanelContent(
      this.context.editorPointer,
      this.actionName,
    );

    if (!panelContent) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionPanelContentMissing,
      ).withActionName(this.actionName);
    }

    return {
      panelContent,
      cssUrl: this.action?.execution?.panel?.inline?.code?.cssUrl,
      panelSize: this.action.execution.panel?.inline?.size,
      panelMode: this.action.execution.panel?.inline?.panelMode,
    };
  }
}

class PanelApiFactory {
  static createPanelAPI(
    action: CustomManifestAction,
    actionName: string,
    context: PlatformContext,
  ): TPanelAPI {
    let panelData = action?.execution?.panel?.inline;

    if (
      action?.execution?.panel?.panelType === ACTIONS.PANEL_TYPE.panelId &&
      action.execution.panel.panelId
    ) {
      panelData = context.componentsAPI.panels.getPanelDefinitionByPanelId(
        context.editorPointer,
        action.execution.panel.panelId,
      );
    }

    const updatedAction = {
      ...action,
      execution: {
        ...action.execution,
        panel: {
          panelType: ACTIONS.PANEL_TYPE.inline,
          inline: {
            ...(action.execution.panel?.inline || {}),
            ...(panelData || {}),
          },
        },
      },
    };

    const panelContentType = panelData?.contentType;

    if (panelContentType === ACTIONS.PANEL_CONTENT_TYPE.url) {
      const urlPanelAPI = new UrlPanelAPI(updatedAction, actionName, context);

      const appData = getAppData(
        context.editorPointer,
        context.documentServicesAPI,
        context.editorPointerAPI,
      );

      if (!appData) {
        return urlPanelAPI;
      }

      const compData = getComponentAppData(
        context.editorPointer,
        context.documentServicesAPI,
        context.editorPointerAPI,
      );

      const componentType =
        appData.components?.find(
          ({ componentId }) => componentId === compData.componentId,
        )?.type || '';

      const isTpa = [
        ComponentType.PAGE_OUT_OF_IFRAME,
        ComponentType.WIDGET_OUT_OF_IFRAME,
        ComponentType.WIDGET,
        ComponentType.PAGE,
        // @ts-expect-error wrong DM types
      ].includes(componentType);

      if (isTpa) {
        return new TpaUrlPanelAPI(updatedAction, actionName, context);
      }

      return urlPanelAPI;
    } else if (panelContentType === ACTIONS.PANEL_CONTENT_TYPE.code) {
      return new CodePanelAPI(updatedAction, actionName, context);
    }

    return new UnknownPanelAPI(action);
  }
}

class DataAPI extends CustomisationAPI implements IDataAPI {
  public apiType: typeof ACTIONS.ACTION_TYPE.data = ACTIONS.ACTION_TYPE.data;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    private readonly context: PlatformContext,
  ) {
    super(action);
  }

  isValidExecution() {
    const dataItemKey = this.action?.execution?.data?.dataItemKey;
    const componentData = this.context.componentsAPI.components.getData(
      this.context.editorPointer,
    );
    if (!dataItemKey || !componentData) {
      return false;
    }
    return !!componentData[dataItemKey];
  }

  getCustomisation() {
    if (!this.isValidExecution()) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionExecutionTypeIsNotSupported,
        `Action ${this.actionName} is not a valid data action for this component.`,
      ).withActionName(this.actionName);
    }

    const dataItemKey = this.action.execution.data!.dataItemKey!;
    const panelApi = createActionPanelAPI(
      this.action,
      this.actionName,
      this.context,
      {
        namespace: 'data',
        itemKeys: [dataItemKey],
      },
    );

    return {
      panelContent: panelApi.getContent(),
    };
  }
}

class CssPropertyAPI extends CustomisationAPI implements ICssPropertyAPI {
  public apiType: typeof ACTIONS.ACTION_TYPE.cssProperty =
    ACTIONS.ACTION_TYPE.cssProperty;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    private readonly context: PlatformContext,
  ) {
    super(action);
  }

  isValidExecution() {
    const cssPropertyKey = this.action?.execution?.cssProperty?.cssPropertyKey;
    const componentStyle = this.context.componentsAPI.components.getStyle(
      this.context.editorPointer,
    );
    if (!cssPropertyKey || !componentStyle) {
      return false;
    }
    return !!componentStyle.cssProperties[cssPropertyKey];
  }

  getCustomisation() {
    if (!this.isValidExecution()) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionExecutionTypeIsNotSupported,
        `Action ${this.actionName} is not a valid style action for this component.`,
      ).withActionName(this.actionName);
    }

    const cssPropertyKey = this.action.execution.cssProperty!.cssPropertyKey!;
    const panelApi = createActionPanelAPI(
      this.action,
      this.actionName,
      this.context,
      {
        namespace: 'cssProperties',
        itemKeys: [cssPropertyKey],
      },
    );

    return {
      panelContent: panelApi.getContent(),
    };
  }
}

class CssCustomPropertyAPI
  extends CustomisationAPI
  implements ICssCustomPropertyAPI
{
  public apiType: typeof ACTIONS.ACTION_TYPE.cssCustomProperty =
    ACTIONS.ACTION_TYPE.cssCustomProperty;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    private readonly context: PlatformContext,
  ) {
    super(action);
  }

  isValidExecution() {
    const cssCustomPropertyKey =
      this.action?.execution?.cssCustomProperty?.cssCustomPropertyKey;
    const componentStyle = this.context.componentsAPI.components.getStyle(
      this.context.editorPointer,
    );
    if (!cssCustomPropertyKey || !componentStyle) {
      return false;
    }

    return !!componentStyle.cssCustomProperties[cssCustomPropertyKey];
  }

  getCustomisation() {
    if (!this.isValidExecution()) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionExecutionTypeIsNotSupported,
        `Action ${this.actionName} is not a valid style action for this component.`,
      ).withActionName(this.actionName);
    }

    const cssPropertyKey =
      this.action.execution.cssCustomProperty!.cssCustomPropertyKey!;
    const panelApi = createActionPanelAPI(
      this.action,
      this.actionName,
      this.context,
      {
        namespace: 'cssCustomProperties',
        itemKeys: [cssPropertyKey],
      },
    );

    return {
      panelContent: panelApi.getContent(),
    };
  }
}

class GroupsAPI extends CustomisationAPI implements IGroupAPI {
  public apiType: typeof ACTIONS.ACTION_TYPE.displayGroup =
    ACTIONS.ACTION_TYPE.displayGroup;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    private readonly context: PlatformContext,
  ) {
    super(action);
  }

  private getGroupItems(
    groupKey: string,
    groupType: Exclude<
      ValueOf<typeof DISPLAY_GROUPS.GROUP_TYPE>,
      | typeof DISPLAY_GROUPS.GROUP_TYPE.UNKNOWN_GroupType
      | typeof DISPLAY_GROUPS.GROUP_TYPE.arrayItem
    >,
  ): string[] {
    const groups = this.context.componentsAPI.components.getDisplayGroups(
      this.context.editorPointer,
    );
    const foundGroup: DisplayGroupItem = groups[groupKey];
    // @ts-expect-error TBD fix
    if (!foundGroup || !foundGroup[groupType]) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionGroupKeyIsMissing,
        `Group with key ${groupKey} not found for action ${this.actionName}`,
      ).withActionName(this.actionName);
    }
    // @ts-expect-error TBD fix
    const groupContent = foundGroup[groupType];

    if (!groupContent || typeof groupContent !== 'object') {
      return [];
    }

    return Object.values(groupContent).flatMap((value) => {
      if (Array.isArray(value) || typeof value === 'string') {
        return value;
      }
      return [];
    });
  }

  private getGroupNamespace(
    groupType: string,
    groupItems: string[],
  ): 'data' | 'cssProperties' | 'cssCustomProperties' {
    if (groupType === DISPLAY_GROUPS.GROUP_TYPE.cssDataType) {
      return 'cssCustomProperties';
    }
    if (groupType === DISPLAY_GROUPS.GROUP_TYPE.dataType) {
      return 'data';
    }

    const firstItemKey = groupItems[0];
    if (!firstItemKey) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionGroupKeyIsMissing,
        `Group ${groupType} is empty for action ${this.actionName}`,
      ).withActionName(this.actionName);
    }
    const data = this.context.componentsAPI.components.getData(
      this.context.editorPointer,
    );
    if (data?.[firstItemKey]) {
      return 'data';
    }
    const style = this.context.componentsAPI.components.getStyle(
      this.context.editorPointer,
    );

    // TODO fix path inside style after changes in ComponentService
    if (style.cssProperties[firstItemKey]) {
      return 'cssProperties';
    }
    // TODO fix path inside style after changes in ComponentService
    if (style.cssCustomProperties[firstItemKey]) {
      return 'cssCustomProperties';
    }

    throw createEditorPlatformEditActionsError(
      EditorPlatformEditActionsErrorCode.EditActionExecutionTypeIsNotSupported,
      `Could not determine namespace for group ${groupType} for action ${this.actionName}`,
    ).withActionName(this.actionName);
  }

  isValidExecution() {
    const groupKey = this.action?.execution?.displayGroup?.displayGroupKey;
    const componentGroups =
      this.context.componentsAPI.components.getDisplayGroups(
        this.context.editorPointer,
      );
    if (!groupKey || !componentGroups) {
      return false;
    }
    return !!componentGroups[groupKey] && !!componentGroups[groupKey].groupType;
  }

  getCustomisation() {
    if (!this.isValidExecution()) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionExecutionTypeIsNotSupported,
        `Action ${this.actionName} is not a valid group action for this component.`,
      ).withActionName(this.actionName);
    }

    const groupKey = this.action.execution.displayGroup!.displayGroupKey!;
    const componentGroups =
      this.context.componentsAPI.components.getDisplayGroups(
        this.context.editorPointer,
      );
    const groupType = componentGroups[groupKey].groupType;

    if (
      !groupType ||
      groupType === DISPLAY_GROUPS.GROUP_TYPE.UNKNOWN_GroupType ||
      groupType === DISPLAY_GROUPS.GROUP_TYPE.arrayItem
    ) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionGroupTypeIsMissing,
        `Group with type ${groupKey} is not a valid group`,
      ).withActionName(this.actionName);
    }

    const itemKeys = this.getGroupItems(groupKey, groupType);
    const namespace = this.getGroupNamespace(groupType, itemKeys);

    const panelApi = createActionPanelAPI(
      this.action,
      this.actionName,
      this.context,
      { namespace, itemKeys },
    );

    return {
      panelContent: panelApi.getContent(),
    };
  }
}

class EventAPI extends CustomisationAPI implements IEventAPI {
  public apiType: typeof ACTIONS.ACTION_TYPE.event = ACTIONS.ACTION_TYPE.event;
  readonly appData?: AppData;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    readonly context: PlatformContext,
  ) {
    super(action);
    this.appData = getAppData(
      context.editorPointer,
      context.documentServicesAPI,
      context.editorPointerAPI,
    );
  }

  getCustomisation() {
    if (!this.appData) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionAppDataIsMissing,
      ).withActionName(this.actionName);
    }
    return () =>
      this.context.documentServicesAPI.platform.notifyApplicationByAppDefId(
        this.appData!.appDefinitionId!,
        {
          eventType: this.action.execution.event?.event!,
          eventPayload: {
            editorPointer: this.context.editorPointer,
            id: this.actionName,
            /**
             * The EventAction is redefined in `ooi-metadata-resolver` to allow `payload`.
             */
            // @ts-expect-error `payload` can only come from OOI statics, it is not defined in builder schema
            ...(this.action.execution.event?.payload || {}),
          },
        },
      );
  }
}

export class ForwardAPI extends CustomisationAPI implements IForwardAPI {
  public apiType: typeof ACTIONS.ACTION_TYPE.forward =
    ACTIONS.ACTION_TYPE.forward;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    private readonly context: PlatformContext,
  ) {
    super(action);
  }

  getCustomisation() {
    const forwardAction = new ForwardAction(
      this.action,
      this.actionName,
      this.context,
    );
    const target = forwardAction.getTarget();

    return {
      targetEditorPointer: target.context.editorPointer,
    };
  }
}

export function createCustomisationAPI(
  action: CustomManifestAction | Action,
  actionName: string,
  context: PlatformContext,
): ICustomisationAPI {
  if (!isCustomManifestAction(action)) {
    return new CustomisationEmptyAPI();
  }

  switch (action.execution.actionType) {
    case ACTIONS.ACTION_TYPE.panel:
      return PanelApiFactory.createPanelAPI(action, actionName, context);
    case ACTIONS.ACTION_TYPE.data:
      return new DataAPI(action, actionName, context);
    case ACTIONS.ACTION_TYPE.cssCustomProperty:
      return new CssCustomPropertyAPI(action, actionName, context);
    case ACTIONS.ACTION_TYPE.cssProperty:
      return new CssPropertyAPI(action, actionName, context);
    case ACTIONS.ACTION_TYPE.displayGroup:
      return new GroupsAPI(action, actionName, context);
    case ACTIONS.ACTION_TYPE.event:
      return new EventAPI(action, actionName, context);
    case ACTIONS.ACTION_TYPE.forward:
      return new ForwardAPI(action, actionName, context);
    default:
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionExecutionTypeIsNotSupported,
      ).withActionName(actionName);
  }
}
